import React, { useState, useEffect } from "react";
import api from "../api/client";
import config from "../config";


export default function InventoryReport() {
  const [sku, setSku] = useState("");
  const [shipperId, setShipperId] = useState("");
  const [location, setLocation] = useState("");
  const [tableData, setTableData] = useState([]);
  const [exportType, setExportType] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/reports/inventory");
      let data = res.data?.data || [];

      if (sku.trim()) {
        data = data.filter((x) =>
          x.SKU?.toLowerCase().includes(sku.toLowerCase())
        );
      }

      if (shipperId.trim()) {
        data = data.filter((x) =>
          x.ShipperID?.toString().includes(shipperId)
        );
      }

      if (location.trim()) {
        data = data.filter((x) => {
          const loc = String(
            x.Location ?? x.location ?? x.LOCATION ?? ""
          ).toLowerCase();
          return loc.includes(location.toLowerCase());
        });
      }

      setTableData(data);
    } catch (err) {
      alert("Failed to fetch inventory report");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleSearch();
  }, []);

  const handleExport = () => {
  if (!exportType) {
    alert("Please select an export format");
    return;
  }

  const BASE = config.backendUrl; // 🔑 NO IIS rewrite needed

  if (exportType === "CSV") {
    window.open(
      `${BASE}/api/reports/inventory/export/csv`,
      "_blank"
    );
  }

  if (exportType === "EXCEL") {
    window.open(
      `${BASE}/api/reports/inventory/export/excel`,
      "_blank"
    );
  }

  if (exportType === "PPT") {
    window.open(
      `${BASE}/api/reports/inventory/export/ppt`,
      "_blank"
    );
  }

  setExportType("");
};


  return (
    <div style={styles.page}>
      {/* HEADER */}
      <div style={styles.header}>
        <h2 style={styles.title}>Inventory Report</h2>
        <p style={styles.subtitle}>
          View and export real-time warehouse inventory
        </p>
      </div>

      {/* FILTER CARD */}
      <div style={styles.card}>
        <div style={styles.grid}>
          <div>
            <label style={styles.label}>SKU</label>
            <input
              style={styles.input}
              placeholder="Enter SKU"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
            />
          </div>

          <div>
            <label style={styles.label}>Shipper ID</label>
            <input
              style={styles.input}
              placeholder="Enter Shipper ID"
              value={shipperId}
              onChange={(e) => setShipperId(e.target.value)}
            />
          </div>

          <div>
            <label style={styles.label}>Location</label>
            <input
              style={styles.input}
              placeholder="Enter Location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>

          <div style={{ alignSelf: "end" }}>
            <button style={styles.searchBtn} onClick={handleSearch}>
              {loading ? "Searching..." : "Search"}
            </button>
          </div>
        </div>

        <div style={styles.exportRow}>
          <select
            style={styles.input}
            value={exportType}
            onChange={(e) => setExportType(e.target.value)}
          >
            <option value="">Export Format</option>
            <option value="CSV">CSV</option>
            <option value="EXCEL">Excel</option>
            <option value="PPT">PowerPoint</option>
          </select>

          <button style={styles.exportBtn} onClick={handleExport}>
            Export Report
          </button>
        </div>
      </div>

      {/* TABLE CARD */}
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Inventory Details</h3>

        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>SKU</th>
                <th style={styles.th}>Shipper ID</th>
                <th style={styles.th}>Shipper Name</th>
                <th style={styles.th}>Location</th>
                <th style={{ ...styles.th, textAlign: "center" }}>
                  On Hand
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" style={styles.emptyCell}>
                    Loading inventory data...
                  </td>
                </tr>
              ) : tableData.length === 0 ? (
                <tr>
                  <td colSpan="5" style={styles.emptyCell}>
                    No inventory records found
                  </td>
                </tr>
              ) : (
                tableData.map((row, i) => (
                  <tr
                    key={i}
                    style={{
                      ...(i % 2 === 0 ? styles.rowEven : styles.rowOdd),
                    }}
                  >
                    <td style={styles.td}>{row.SKU}</td>
                    <td style={styles.td}>{row.ShipperID}</td>
                    <td style={styles.td}>{row.ShipperName}</td>
                    <td style={styles.td}>
                      {row.Location ?? row.location ?? row.LOCATION}
                    </td>
                    <td
                      style={{
                        ...styles.td,
                        textAlign: "center",
                        fontWeight: 600,
                      }}
                    >
                      {row.OnHand}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* =========================
        UI STYLES ONLY
========================= */

const styles = {
  page: {
    padding: "2rem",
    background: "#f4f6fa",
    minHeight: "100vh",
    fontFamily: "Inter, system-ui, sans-serif",
  },
  header: { textAlign: "center", marginBottom: "2rem" },
  title: { fontSize: "2.1rem", fontWeight: 700, margin: 0 },
  subtitle: { color: "#6b7280", marginTop: "0.5rem" },

  card: {
    background: "#fff",
    borderRadius: "14px",
    padding: "1.6rem",
    marginBottom: "1.6rem",
    boxShadow: "0 8px 24px rgba(0,0,0,0.05)",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "1.2rem",
  },

  label: { fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.3rem" },

  input: {
    width: "100%",
    padding: "0.7rem",
    borderRadius: "10px",
    border: "1px solid #d1d5db",
    fontSize: "0.9rem",
  },

  searchBtn: {
    width: "50%",
    padding: "0.75rem",
    borderRadius: "10px",
    background: "#dc2626",
    color: "#fff",
    border: "none",
    fontWeight: 600,
    cursor: "pointer",
  },

  exportRow: { display: "flex", gap: "1rem", marginTop: "1.5rem" },

  exportBtn: {
    padding: "0.75rem 1.6rem",
    borderRadius: "10px",
    background: "#dc2626",
    color: "#fff",
    border: "none",
    fontWeight: 600,
    cursor: "pointer",
  },

  sectionTitle: { marginBottom: "1rem", fontWeight: 600 },

  tableWrapper: {
    overflowX: "auto",
    borderRadius: "12px",
    border: "1px solid #e5e7eb",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "700px",
  },

  th: {
    padding: "12px",
    background: "#f9fafb",
    fontWeight: 600,
    borderBottom: "1px solid #e5e7eb",
    textAlign: "left",
  },

  td: {
    padding: "12px",
    borderBottom: "1px solid #f1f5f9",
  },

  rowEven: { background: "#fff" },
  rowOdd: { background: "#f9fafb" },

  emptyCell: {
    padding: "1.5rem",
    textAlign: "center",
    color: "#6b7280",
  },
};
